/**
 * F17 (Wave 2C) — admin booking detail page.
 *
 * Read-only for everyone except super_admin (who gets the
 * `bookings:override` permission and the force-transition action). Mirrors
 * the venue-staff `VenueBookingDetailPage` UX but without the accept /
 * decline / cancel / complete / no-show / reschedule buttons — admin
 * intervention is the last-resort recovery path.
 */
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  AuditLogTimeline,
  Banner,
  Button,
  Card,
  Chip,
  CurrencyDisplay,
  EmptyState,
  Skeleton,
  StateRibbon,
  useToast,
} from '@bravemove/ui-web';
import type { AuditEvent, AuditEventKind } from '@bravemove/ui-web';
import type { BookingDetail, BookingStatus, ForceTransitionInput } from '@bravemove/types';
import { getAdminBooking, forceTransitionAdminBooking } from '../../api/adminBookings';
import { ForceTransitionDialog } from '../../components/ForceTransitionDialog';
import { PaymentStatusChip } from '../../components/PaymentStatusChip';
import { PaymentMarksHistoryPanel } from '../../components/PaymentMarksHistoryPanel';
import { DisputeHistoryPanel } from '../../components/DisputeHistoryPanel';
// F27 (Wave 2E) — WhatsApp send-attempts panel for incident response.
import { WhatsAppDeliveryLogPanel } from '../../components/whatsapp/WhatsAppDeliveryLogPanel';
import { useAuth } from '../../auth/useAuth';

function formatLocalTime(iso: string, tz: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: tz,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Maps a F17 `bookings.history` row to the shared `AuditEvent` shape.
 * - `kind` is derived from the target status: terminal states map to
 *   `'delete'` (cancelled/no_show) or `'create'` (completed); active
 *   transitions map to `'update'`. Force-transitions become `'alert'`
 *   regardless of the target so they read at-a-glance.
 * - `actor` shows the `actorKind` label; surface is appended into details.
 */
function statusKind(to: BookingStatus, actorKind: string): AuditEventKind {
  if (actorKind === 'admin') return 'alert';
  switch (to) {
    case 'completed':
      return 'create';
    case 'cancelled':
    case 'no_show':
      return 'delete';
    default:
      return 'update';
  }
}

function actorDisplayName(actorKind: string): string {
  switch (actorKind) {
    case 'booker':
      return 'Booker';
    case 'venue_staff':
      return 'Venue staff';
    case 'admin':
      return 'Administrator';
    case 'system':
      return 'System';
    default:
      return actorKind;
  }
}

function mapHistoryToAuditEvents(history: BookingDetail['history']): readonly AuditEvent[] {
  return history.map((h, i) => {
    const target = h.toStatus;
    const from = h.fromStatus;
    const summary =
      from === null ? `set status to ${target}` : `transitioned from ${from} to ${target}`;
    const detailLine = `${h.actorKind} · ${h.surface}`;
    return {
      id: `${h.createdAt}-${i}`,
      at: h.createdAt,
      kind: statusKind(target, h.actorKind),
      summary,
      actor: { name: actorDisplayName(h.actorKind) },
      targetLabel: target,
      details: h.reason ? `${detailLine} — ${h.reason}` : detailLine,
    };
  });
}

export function AdminBookingDetailPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ readonly id: string }>();
  const auth = useAuth();
  const toast = useToast();

  const [booking, setBooking] = useState<BookingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forceOpen, setForceOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    let active = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- standard load-on-mount
    setLoading(true);
    getAdminBooking(id)
      .then((b) => {
        if (!active) return;
        if (b === null) setNotFound(true);
        else setBooking(b);
      })
      .catch((err) => {
        if (!active) return;
        const msg = err instanceof Error ? err.message : 'load';
        setError(msg);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  if (!id) return <Navigate to="/" replace />;

  const canForceTransition = auth.adminUser?.permissions.includes('bookings:override') ?? false;

  const handleForceTransition = async (input: ForceTransitionInput) => {
    if (!booking) return;
    try {
      const updated = await forceTransitionAdminBooking(booking.id, input);
      setBooking(updated);
      toast.push({ tone: 'success', title: t('booking:admin.force_transition.success') });
    } catch {
      toast.push({ tone: 'danger', title: t('booking:admin.force_transition.error') });
      throw new Error('FORCE_TRANSITION_FAILED');
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl flex flex-col gap-4" data-testid="admin-booking-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  // BP-13 — the load-error branch must run BEFORE the not-found guard: a
  // non-404 failure leaves `booking` null, and checking notFound/!booking
  // first rendered the misleading "not found" EmptyState on 500/network errors.
  if (error) {
    return (
      <div className="max-w-3xl">
        <Banner tone="danger" title={t('booking:admin.detail.loadError')}>
          {error}
        </Banner>
      </div>
    );
  }

  if (notFound || !booking) {
    return (
      <div className="max-w-3xl">
        <EmptyState
          title={t('booking:admin.detail.notFound')}
          action={
            <Button variant="ghost" onClick={() => navigate('/')}>
              {t('booking:admin.detail.back')}
            </Button>
          }
        />
      </div>
    );
  }

  const isBlockout = booking.kind === 'blockout';
  const showRefundBanner =
    !isBlockout &&
    booking.refund !== null &&
    booking.refund.refundAmount > 0 &&
    booking.paymentStatus !== 'refunded';

  return (
    <div className="max-w-3xl flex flex-col gap-4" data-testid="admin-booking-detail-page">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <button
            type="button"
            className="text-[12px] text-accent hover:underline self-start focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-accent/40 rounded"
            onClick={() => navigate('/')}
          >
            {t('booking:admin.detail.back')}
          </button>
          <h1 className="text-h1 font-bold text-ink">{t('booking:admin.detail.title')}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isBlockout ? <PaymentStatusChip state={booking.paymentStatus} /> : null}
          {booking.kind === 'walk_in' ? (
            <Chip variant="walk_in" data-testid="admin-booking-walk-in-chip">
              {t('booking:walk_in.chip_label')}
            </Chip>
          ) : null}
        </div>
      </header>

      {/* Hero block: multi-node lifecycle backbone with forked endpoints. */}
      <Card>
        <StateRibbon current={booking.status} />
      </Card>

      {showRefundBanner && booking.refund ? (
        <div data-testid="admin-refund-eligible-banner">
          <Banner tone="info">
            <div className="flex items-center gap-2 flex-wrap">
              <span>
                {t('booking:notification.refund_eligible_banner', {
                  amount: '',
                  percent: booking.refund.refundPercent,
                })}
              </span>
              <CurrencyDisplay
                amount={booking.refund.refundAmount}
                currency={booking.price.priceCurrency}
              />
            </div>
          </Banner>
        </div>
      ) : null}

      <Card>
        <div className="flex flex-col gap-3">
          <h2 className="text-h2 font-bold text-ink">
            {isBlockout ? t('booking:admin.detail.blockoutTitle') : t('booking:admin.detail.title')}
          </h2>
          <dl className="grid grid-cols-[140px_1fr] gap-y-2 gap-x-3 text-[12.5px]">
            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('booking:admin.detail.idLabel')}
            </dt>
            <dd className="text-ink font-mono tabular-nums">{booking.id}</dd>

            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('booking:admin.detail.timeLabel')}
            </dt>
            <dd className="text-ink">
              {formatLocalTime(booking.startAt, booking.venueTimezone)} —{' '}
              {formatLocalTime(booking.endAt, booking.venueTimezone)}
            </dd>

            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('booking:admin.detail.venueLabel')}
            </dt>
            <dd className="text-ink">{booking.venueName}</dd>

            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('booking:admin.detail.courtLabel')}
            </dt>
            <dd className="text-ink">{booking.subVenueName}</dd>

            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('booking:admin.detail.sportLabel')}
            </dt>
            <dd className="text-ink">{booking.sportName}</dd>

            {!isBlockout ? (
              <>
                <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
                  {t('booking:admin.detail.bookerLabel')}
                </dt>
                <dd className="text-ink">
                  {booking.hostDisplayName ?? t('booking:admin.detail.anonymousBooker')}
                </dd>

                <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
                  {t('booking:admin.detail.priceLabel')}
                </dt>
                <dd className="text-ink">
                  <CurrencyDisplay
                    amount={booking.price.priceAmount}
                    currency={booking.price.priceCurrency}
                  />
                </dd>
              </>
            ) : null}

            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('booking:admin.detail.createdLabel')}
            </dt>
            <dd className="text-ink-2 text-[11.5px] font-mono tabular-nums">{booking.createdAt}</dd>

            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('booking:admin.detail.updatedLabel')}
            </dt>
            <dd className="text-ink-2 text-[11.5px] font-mono tabular-nums">{booking.updatedAt}</dd>
          </dl>

          {canForceTransition ? (
            <div className="border-t border-hairline pt-3">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setForceOpen(true)}
                data-testid="admin-force-transition-cta"
              >
                {t('booking:action.force_transition')}
              </Button>
            </div>
          ) : null}
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-3">
          <h2 className="text-h2 font-bold text-ink">{t('booking:admin.detail.timeline')}</h2>
          <div data-testid="admin-history">
            <AuditLogTimeline events={mapHistoryToAuditEvents(booking.history)} />
          </div>
        </div>
      </Card>

      {/* F20 — payment marks history + previous-disputes panels, read-only
          on the admin booking detail. PaymentStatusChip is already in the
          header (added by the F35 design polish wave); the force-transition
          dialog above is the only admin write path here — F20 panels add
          context, not actions. */}
      {!isBlockout ? (
        <PaymentMarksHistoryPanel
          marks={booking.recentPaymentMarks ?? []}
          title={t('payment:detail.payment_history', { defaultValue: 'Payment history' })}
        />
      ) : null}
      {!isBlockout && booking.openDispute ? (
        <DisputeHistoryPanel
          disputes={[booking.openDispute]}
          title={t('payment:detail.open_dispute', { defaultValue: 'Open dispute' })}
        />
      ) : null}

      {/* F27 (Wave 2E) — WhatsApp delivery log for this booking. Hidden
          gracefully when the actor lacks whatsapp:read (component degrades
          silently on 403). */}
      {!isBlockout ? <WhatsAppDeliveryLogPanel bookingId={booking.id} /> : null}

      <ForceTransitionDialog
        booking={booking}
        open={forceOpen}
        onClose={() => setForceOpen(false)}
        onConfirm={handleForceTransition}
      />
    </div>
  );
}

export default AdminBookingDetailPage;
